import { useIsSSR } from 'react-aria';

import { SelectBase, SelectBaseProps } from './SelectBase/SelectBase';

import { Description } from '../Description/Description';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { Label } from '../Label/Label';

import { Icon } from '@zapier/zinnia-icons';
import {
  SelectBaseListBox,
  type SelectBaseListBoxProps,
} from './SelectBase/ListBox';
import { SelectBasePopover } from './SelectBase/Popover';
import { SelectBaseValue } from './SelectBase/SelectValue';
import { SelectBaseTriggerButton } from './SelectBase/TriggerButton';
import { VisuallyHidden } from '../../display/VisuallyHidden';

import styles from './Select.module.scss';

import type { FieldProps, Key } from '../../../types';
export type { Key };

export type SelectProps<T extends object> = Omit<
  SelectBaseProps<T>,
  'children'
> &
  FieldProps & {
    children: SelectBaseListBoxProps<T>['children'];
    /** The content to label the Select. */
    label: string;
    /**
     * Sets the selected value. For use in a controlled component. To be used
     * alongside `onChange`.
     */
    selectedKey?: Key | null;
  };

/**
 * Root component wrapping around the `Select` subcomponents. Built using React
 * Aria's [Select](https://react-spectrum.adobe.com/react-aria/Select.html)
 * component.
 */
export const Select = <T extends object>({
  children,
  errorMessage,
  helpText,
  isInvalid = false,
  isLabelHidden = false,
  label,
  size = 'medium',
  ...props
}: SelectProps<T>) => {
  const isSSR = useIsSSR();

  if (isSSR) {
    // react-aria Select renders hidden children in order to build the
    // collection even when the popover is not open. react-aria Hidden wrapps
    // its children in a `template` tag in SSR environments. The presence of
    // that `template` creates errors during hydration.
    //
    // https://github.com/adobe/react-spectrum/blob/17751bb75cf4b0500e86abe9e1b4561d140f9486/packages/react-aria-components/src/Select.tsx#L135
    //
    // https://github.com/adobe/react-spectrum/blob/main/packages/react-aria-components/src/utils.tsx#L326
    //
    // We return nothing while the component is being server side rendered or
    // hydrated on the client to prevent that error.
    return null;
  }

  return (
    <SelectBase {...props} isInvalid={isInvalid} size={size}>
      {isLabelHidden ? (
        <VisuallyHidden>
          <Label>{label}</Label>
        </VisuallyHidden>
      ) : (
        <Label>
          {isInvalid && (
            <Icon
              color="StatusErrorStronger"
              isBlock
              name="formXCircle"
              size={18}
            />
          )}
          {label}
        </Label>
      )}
      <SelectBaseTriggerButton>
        <SelectBaseValue />
        <Icon
          className={styles.dropdownIcon}
          name="arrowDropdown"
          color="TextWeakest"
          size={size === 'compact' || size === 'medium' ? 20 : 24}
        />
      </SelectBaseTriggerButton>
      {isInvalid && errorMessage ? (
        <ErrorMessage isCompact={size === 'compact'}>
          {errorMessage}
        </ErrorMessage>
      ) : (
        helpText && <Description>{helpText}</Description>
      )}
      <SelectBasePopover>
        <SelectBaseListBox>{children}</SelectBaseListBox>
      </SelectBasePopover>
    </SelectBase>
  );
};
